import { useComputedColorScheme } from '@mantine/core';
import { DEFAULT_QR_CODE_COLOR } from '@/theme';

function parseHex(hex: string): [number, number, number] | null {
  const normalized = hex.replace('#', '').trim();
  if (normalized.length !== 6 || !/^[0-9a-fA-F]+$/.test(normalized)) return null;
  return [
    parseInt(normalized.slice(0, 2), 16),
    parseInt(normalized.slice(2, 4), 16),
    parseInt(normalized.slice(4, 6), 16),
  ];
}

function toHex(r: number, g: number, b: number): string {
  const h = (n: number) =>
    Math.max(0, Math.min(255, Math.round(n)))
      .toString(16)
      .padStart(2, '0');
  return `#${h(r)}${h(g)}${h(b)}`;
}

/** Mix two hex colors; `amount` is the weight of `toward` (0–1). */
export function mixHex(hex: string, toward: string, amount: number): string {
  const a = parseHex(hex);
  const b = parseHex(toward);
  if (!a || !b) return hex;
  const t = Math.max(0, Math.min(1, amount));
  return toHex(
    a[0] + (b[0] - a[0]) * t,
    a[1] + (b[1] - a[1]) * t,
    a[2] + (b[2] - a[2]) * t
  );
}

/**
 * Corporate / brand colors (e.g. #5D7723) are often too dark for icons and
 * selected nav labels on dark surfaces. Lighten them in dark mode.
 */
export function brandForeground(
  hex: string | undefined,
  isDark: boolean,
  fallback: string = DEFAULT_QR_CODE_COLOR
): string {
  const color = hex?.trim() || fallback;
  if (!isDark) return color;
  return mixHex(color, '#F1F5F9', 0.58);
}

/** Scheme-aware brand color for Tabler icons / text accents. */
export function useBrandForeground(accent?: string): string {
  const scheme = useComputedColorScheme('light');
  return brandForeground(accent, scheme === 'dark');
}
